import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { useLoader } from "../../../base/context/LoaderProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { WalletApiService } from "../../../service/seller/walletApiService";
import { clearAuthLocalStorage } from "../../../storage/localStorage";
import { formatDateOnly, formatDate, downloadCsv, jsonToCsv } from "../../../helper/utils";
import PaymentPopup from "../../../component/views/seller/wallet/paymentPopup";
import RechargeRequestModal from "../../../component/views/seller/wallet/rechargeRequestModal";
import Pagination from "../../../component/library/pagination";
import { RiDownload2Fill } from "react-icons/ri";
import { FaWallet, FaArrowDown, FaArrowUp, FaUndo } from "react-icons/fa";
import "./style.css";

export default function Wallet() {
  const message = useMessage();
  const loader = useLoader();
  const navigate = useNavigate();
  const location = useLocation();

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: "", totalPages: "", totalItems: "" });
  const [transaction, setTransaction] = useState([]);
  const [type, setType] = useState("");
  const [wallet, setWallet] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [numberOfDays, setNumberOfDays] = useState(0);
  const [addAmount, setAddAmount] = useState(false);
  const [rechargeRequestOpen, setRechargeRequestOpen] = useState(false);
  const [rechargeRequests, setRechargeRequests] = useState([]);
  const [rechargeRequestsPage, setRechargeRequestsPage] = useState(1);
  const [rechargeRequestsPages, setRechargeRequestsPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [autoPay, setAutoPay] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [chartYear, setChartYear] = useState(new Date().getFullYear().toString());

  // Generate year options dynamically (last 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchRechargeRequests();
  }, [rechargeRequestsPage]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const date = new Date();
    let nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);

    if (search.get('startDate')) {
      setStartDate(date);
      setEndDate(nextDay);
    }
    handleWallet();
    handleChartData();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      handleTransactions();
      handleCalculate();
    } else if (!startDate && !endDate) {
      handleTransactions();
      handleCalculate();
    }

    const search = new URLSearchParams(location.search);
    if (search.size) {
      navigate('/seller/wallet');
    }
  }, [type, startDate, endDate, page]);

  useEffect(() => {
    handleChartData();
  }, [chartYear]);

  useEffect(() => {
    setAutoPay(wallet?.wallet?.autoPay);
  }, [wallet]);

  let query = `?type=${type}&startDate=${startDate}&endDate=${endDate}&limit=${limit}&page=${page}`;

  const handleTransactions = async () => {
    loader.showLoading(true);
    await WalletApiService.getTransaction(query)
      .then(async (res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          setTransaction(res.data.transactions);
          setPages({
            currentPage: res.data.currentPage,
            totalPages: res.data.totalPages,
            totalItems: res.data.totalTransactions,
          });
          setPage(res.data.currentPage);
        } else if (res.status.code === 402) {
          message.showToast(res.error.message, ToastTypes.ERROR);
          clearAuthLocalStorage();
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
      });
  };

  const handleWallet = async () => {
    await WalletApiService.getWalet()
      .then(async (res) => {
        if (res.status.code === 200) {
          setWallet(res.data);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
      });
  };

  const handleChartData = () => {
    WalletApiService.getChartData(chartYear)
      .then((res) => {
        setChartData(res.data);
      })
      .catch((err) => {
        console.log("Chart data error:", err);
      });
  };

  const handleCalculate = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const difference = end - start;
    const daysDifference = Math.ceil(difference / (1000 * 60 * 60 * 24));
    setNumberOfDays(daysDifference);
  };

  const handleRefresh = () => {
    handleTransactions();
    handleWallet();
    fetchRechargeRequests();
  };

  const fetchRechargeRequests = () => {
    const query = `?page=${rechargeRequestsPage}&limit=5`;
    WalletApiService.getMyRechargeRequests(query)
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          setRechargeRequests(res.data.requests || []);
          setRechargeRequestsPages({
            currentPage: res.data.currentPage || 1,
            totalPages: res.data.totalPages || 1,
            totalItems: res.data.totalItems || 0,
          });
        }
      })
      .catch(() => {});
  };

  const handleAutopay = async () => {
    loader.showLoading(true);
    await WalletApiService.getAutoPay()
      .then(async (res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          message.showToast(res.status.message, ToastTypes.SUCCESS);
          setAutoPay(!autoPay);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
      });
  };

  const handleType = (e) => {
    setType(e.target.value);
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const keys = ["_id", "type", "amount", "balance", "description", "date"];
  let csvContent = "Id,Type,Amount,Final Balance,Description,Date";

  const handleDownloadCsv = () => {
    if (transaction.length) {
      if (numberOfDays <= 30) {
        downloadCsv(jsonToCsv(transaction, keys, csvContent), "IM-cash-transactions.csv");
      } else {
        message.showToast("Maximum limit 30 days", ToastTypes.ERROR);
      }
    } else {
      message.showToast("Data not available to download", ToastTypes.ERROR);
    }
  };

  const handleOrderById = async (item) => {
    const array = item.description.split(" ");
    const id = array[array.length - 1];
    navigate(`/seller/orders/orderDetails/${id}`);
  };

  const handleDateFilter = () => {
    if (startDate && endDate) {
      handleTransactions();
    }
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setType('');
    setPage(1);
  };

  // Chart configuration with dynamic data
  const chartSeries = [
    { name: "Orders", data: chartData?.order || Array(12).fill(0) },
    { name: "Deposit", data: chartData?.deposit || Array(12).fill(0) },
    { name: "Refund", data: chartData?.refund || Array(12).fill(0) },
  ];

  const chartOptions = {
    chart: {
      type: "area",
      height: 220,
      toolbar: { show: false },
      zoom: { enabled: false },
      sparkline: { enabled: false },
    },
    colors: ["#3DD0E5", "#10b981", "#f59e0b"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
    stroke: { width: 2, curve: "smooth" },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
    xaxis: {
      categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      labels: { style: { fontSize: "10px", colors: "#6b7280" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { 
        style: { fontSize: "10px", colors: "#6b7280" },
        formatter: (val) => val >= 1000 ? `${(val/1000).toFixed(1)}k` : val
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "11px",
      markers: { width: 8, height: 8, radius: 2 },
      itemMargin: { horizontal: 8 },
    },
    tooltip: {
      y: { formatter: (val) => `₹${val?.toLocaleString() || 0}` },
    },
    dataLabels: { enabled: false },
  };

  return (
    <div className="sw-page">
      {/* Top Row - 50% Cards | 50% Chart */}
      <div className="sw-top-row">
        {/* Left Side - 4 Cards */}
        <div className="sw-cards-section">
          <div className="sw-stat-cards-grid">
            <div className="sw-stat-card sw-stat-consumed">
              <div className="sw-stat-icon"><FaArrowUp /></div>
              <div className="sw-stat-content">
                <span className="sw-stat-value">₹{wallet?.totalConsume?.toLocaleString() || 0}</span>
                <span className="sw-stat-label">Amount Consumed</span>
              </div>
            </div>
            <div className="sw-stat-card sw-stat-deposit">
              <div className="sw-stat-icon"><FaArrowDown /></div>
              <div className="sw-stat-content">
                <span className="sw-stat-value">₹{wallet?.totalDeposit?.toLocaleString() || 0}</span>
                <span className="sw-stat-label">Amount Deposit</span>
              </div>
            </div>
            <div className="sw-stat-card sw-stat-refund">
              <div className="sw-stat-icon"><FaUndo /></div>
              <div className="sw-stat-content">
                <span className="sw-stat-value">₹{wallet?.totalRefund?.toLocaleString() || 0}</span>
                <span className="sw-stat-label">Amount Refund</span>
              </div>
            </div>
            {/* My Card */}
            <div className="sw-my-card">
              <div className="sw-card-header">
                <span className="sw-card-title">My Card</span>
                <div className="sw-recharge-btns">
                  <button className="sw-recharge-btn" onClick={() => setAddAmount(true)}>Recharge</button>
                  <button className="sw-recharge-btn sw-recharge-btn-secondary" onClick={() => setRechargeRequestOpen(true)}>Request Recharge</button>
                </div>
              </div>
              <div className="sw-card-visual">
                <div className="sw-card-inner">
                  <div className="sw-card-name">
                    {wallet?.wallet?.userId?.firstName || ""} {wallet?.wallet?.userId?.lastName || ""}
                  </div>
                  <div className="sw-card-number">{wallet?.wallet?.cardNumber}</div>
                  <div className="sw-card-balance-row">
                    <span className="sw-card-balance">₹{wallet?.wallet?.balance?.toLocaleString() || 0}</span>
                    <div className="sw-autopay-toggle" onClick={handleAutopay}>
                      <div className={`sw-toggle-track ${autoPay ? 'active' : ''}`}>
                        <div className="sw-toggle-thumb"></div>
                      </div>
                      <span className="sw-toggle-label">{autoPay ? 'Auto' : 'Manual'}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="sw-card-info">
                <div className="sw-info-row">
                  <span className="sw-info-label">Plan</span>
                  <span className="sw-info-value">{wallet?.membership?.subscriptionId?.name || 'N/A'}</span>
                </div>
                {/* Currently not showing the valid until date */}
                {/* <div className="sw-info-row">
                  <span className="sw-info-label">Valid Until</span>
                  <span className="sw-info-value">{formatDateOnly(wallet?.membership?.endDate) || 'N/A'}</span>
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Chart */}
        <div className="sw-chart-section">
          <div className="sw-chart-header">
            <span className="sw-section-title">Analysis</span>
            <select 
              className="sw-year-select" 
              value={chartYear} 
              onChange={(e) => setChartYear(e.target.value)}
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="sw-chart-container">
            <Chart options={chartOptions} series={chartSeries} type="area" height={280} />
          </div>
        </div>
      </div>

      {/* Transactions Section */}
      <div className="sw-transactions-section">
        <div className="sw-transactions-header">
          <span className="sw-section-title">IM Cash Transactions</span>
          <div className="sw-transactions-filters">
            <input 
              type="date" 
              className="sw-date-input" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)}
              placeholder="Start Date"
            />
            <input 
              type="date" 
              className="sw-date-input" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="End Date"
            />
            <button className="sw-filter-btn" onClick={handleDateFilter}>Apply</button>
            <button className="sw-clear-btn" onClick={handleClearFilter}>Clear</button>
            <select className="sw-type-select" value={type} onChange={handleType}>
              <option value="">All Types</option>
              <option value="Deposit">Deposit</option>
              <option value="Order">Order</option>
              <option value="Refund">Refund</option>
            </select>
            <button className="sw-download-btn" onClick={handleDownloadCsv} title="Download CSV">
              <RiDownload2Fill />
            </button>
          </div>
        </div>

        <div className="sw-table-wrapper">
          <table className="sw-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Transaction ID</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Balance</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {transaction && transaction.length > 0 ? (
                transaction.map((item, index) => (
                  <tr key={index}>
                    <td>{formatDate(item.date)}</td>
                    <td className="sw-txn-id">{item.transactionId || item._id}</td>
                    <td>
                      <span className={`sw-type-badge sw-type-${item.type?.toLowerCase()}`}>
                        {item.type}
                      </span>
                    </td>
                    <td className={item.type === 'Deposit' || item.type === 'Refund' ? 'sw-amount-positive' : 'sw-amount-negative'}>
                      {item.type === 'Deposit' || item.type === 'Refund' ? '+' : '-'}₹{Math.abs(item.amount)?.toLocaleString()}
                    </td>
                    <td>₹{item.balance?.toLocaleString()}</td>
                    <td 
                      className={item.type === "Order" || item.type === "Refund" ? "sw-clickable" : ""}
                      onClick={item.type === "Order" || item.type === "Refund" ? () => handleOrderById(item) : null}
                    >
                      {item.description}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="sw-no-data">No transactions found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {transaction.length > 0 && (
          <Pagination pages={pages} page={page} setPage={setPage} />
        )}
      </div>

      {/* My Recharge Requests */}
      <div className="sw-recharge-requests-section">
        <div className="sw-recharge-requests-header">
          <span className="sw-section-title">My Recharge Requests</span>
        </div>
        <div className="sw-table-wrapper">
          <table className="sw-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Transaction ID</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Admin Note</th>
              </tr>
            </thead>
            <tbody>
              {rechargeRequests?.length > 0 ? (
                rechargeRequests.map((item) => (
                  <tr key={item._id}>
                    <td>{formatDate(item.created_at)}</td>
                    <td className="sw-txn-id">{item.transactionId}</td>
                    <td>₹{item.amount?.toLocaleString()}</td>
                    <td>
                      <span className={`sw-type-badge sw-type-${item.status}`}>
                        {item.status}
                      </span>
                    </td>
                    <td>{item.adminNote || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="sw-no-data">No recharge requests yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {rechargeRequestsPages.totalItems > 5 && (
          <Pagination pages={rechargeRequestsPages} page={rechargeRequestsPage} setPage={setRechargeRequestsPage} />
        )}
      </div>

      <PaymentPopup
        modalIsOpen={addAmount}
        setIsOpen={setAddAmount}
        handleRefresh={handleRefresh}
      />
      <RechargeRequestModal
        modalIsOpen={rechargeRequestOpen}
        setIsOpen={setRechargeRequestOpen}
        handleRefresh={handleRefresh}
      />
    </div>
  );
}
